// Key metrics — Figma "Frame 2087325086" (node 6384:18117).
//
// A StatCard ("Key metrics") whose body is a single row of lean DS MetricTiles
// (icon + label, prominent value, a <Change/> trend + "vs prev period"),
// separated by full-bleed vertical Dividers. In Customize mode each tile's icon
// is replaced by a drag grip and the tiles can be reordered (persisted via the
// report-layout store).
import { Fragment, Suspense, lazy, useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Change, Divider, MetricTile, Skeleton } from '@clickguard/design-system'
import { ReportSection } from './report-section'
// Lazy: the drawer pulls in recharts (only used by its previews — the live
// charts use nivo), so this keeps recharts off the eagerly-loaded page's main
// bundle. It mounts as a separate chunk when the page renders.
const ChartCustomizationDrawer = lazy(() =>
	import('./chart-customization-drawer').then((m) => ({ default: m.ChartCustomizationDrawer }))
)
import { METRIC_IDS, METRIC_BY_ID, METRIC_POOL, metricSections } from './key-metrics-data'
import { useMetricCards } from '../data/use-analytics-data'
import { formatMetricValue, growthPillProps } from '../data/format-metric'
import { cardsPdfSnapshot } from './pdf/card-snapshot'
import { useAssetStore } from 'store'
import {
	useReportLayoutStore,
	DEFAULT_METRIC_CONFIG
} from '../store/use-report-layout-store'
import { useDragReorder } from './use-drag-reorder'
import { DragGrip } from 'components/data-table-v2'
import { BarChartSquare02 } from './icons'
import { SlotToolGroup } from './slot-tool-group'
import { CustomizeReveal, usePrefersReducedMotion } from './customize-reveal'
import './customize.css'

// Shown when every tile has been hidden from Customize, so the section body would
// otherwise be blank. Mirrors the grid's TableEmptyState (soft layered brand badge
// + title + muted line) and tells the editor what happens on export. The badge
// ring breathes; reduced motion gets a static one.
const MetricsEmptyState = ({ reduced }: { reduced: boolean }) => (
	<motion.div
		initial={reduced ? false : { opacity: 0, y: 8 }}
		animate={{ opacity: 1, y: 0 }}
		transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
		className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-12 text-center"
	>
		<div className="relative flex h-16 w-16 items-center justify-center">
			<motion.span
				aria-hidden
				className="absolute inset-0 rounded-2xl bg-[rgba(85,75,253,0.06)]"
				animate={reduced ? undefined : { opacity: [0.5, 1, 0.5], scale: [0.96, 1, 0.96] }}
				transition={reduced ? undefined : { duration: 3, ease: 'easeInOut', repeat: Infinity }}
			/>
			<span aria-hidden className="absolute inset-[10px] rounded-xl bg-[rgba(85,75,253,0.1)]" />
			<span className="relative flex h-9 w-9 items-center justify-center rounded-[10px] bg-white shadow-sm ring-1 ring-[#ececf5]">
				<BarChartSquare02 className="h-4 w-4 text-[rgb(var(--cg-brand))]" />
			</span>
		</div>
		<div className="space-y-1">
			<p className="text-[15px] font-semibold text-[rgb(var(--cg-text-primary))]">No metrics selected</p>
			<p className="mx-auto max-w-[320px] text-[13px] leading-5 text-gray-500">
				This section will be hidden when the report is exported.
			</p>
		</div>
	</motion.div>
)

export const KeyMetrics = () => {
	// Customize-mode pencil opens the same "Chart block customization" drawer.
	const [customizeOpen, setCustomizeOpen] = useState(false)
	const editMode = useReportLayoutStore((s) => s.editMode)
	const metricOrder = useReportLayoutStore((s) => s.metricOrder)
	const hiddenMetrics = useReportLayoutStore((s) => s.hiddenMetrics)
	const metricConfig = useReportLayoutStore((s) => s.metricConfig)
	const sectionName = useReportLayoutStore((s) => s.sectionNames.metric)
	const setSectionName = useReportLayoutStore((s) => s.setSectionName)
	const initMetricOrder = useReportLayoutStore((s) => s.initMetricOrder)
	const reorderMetric = useReportLayoutStore((s) => s.reorderMetric)
	const setMetricCfg = useReportLayoutStore((s) => s.setMetricCfg)

	// Re-run on entering Customize: enterEdit reseeds the draft order from
	// `committed` (empty until a custom order is saved), which would make
	// reorderMetric a no-op (arrayMove on []) so tiles wouldn't move. Reconcile
	// repopulates to METRIC_IDS while preserving any saved order.
	useEffect(() => {
		initMetricOrder(METRIC_IDS)
	}, [initMetricOrder, editMode])

	const dnd = useDragReorder(reorderMetric)
	const reduced = usePrefersReducedMotion()

	// Live scalar values (+ previous-period growth) for the 16 metrics. Each tile
	// renders its configured metric's cell; the config/order machinery below stays
	// label-based. Falls back to the placeholder value until the query resolves.
	const { data: liveCards } = useMetricCards()
	const currency = useAssetStore((s) => s.asset?.currency) || 'USD'

	// Publish the live, formatted card values for the PDF export (keyed by label),
	// so a downloaded report shows real numbers instead of placeholders. Mirrors
	// the table's gridPdfSnapshot. Covers all metrics, not just the visible tiles.
	useEffect(() => {
		if (!liveCards) return
		const snap: Record<string, { value: string; change: string; direction?: 'up' | 'down'; trend?: 'positive' | 'negative' | 'neutral' }> = {}
		Object.values(METRIC_BY_ID).forEach((m) => {
			const cell = liveCards[m.id]
			if (!cell) return
			const growth = growthPillProps(m.id, cell.value, cell.prevValue)
			snap[m.label] = {
				value: formatMetricValue(cell.value, m.format, currency),
				change: growth.value,
				direction: growth.direction,
				trend: growth.trend
			}
		})
		cardsPdfSnapshot.current = snap
		return () => {
			cardsPdfSnapshot.current = null
		}
	}, [liveCards, currency])

	// Full saved order (fall back to natural), then drop hidden tiles so the rest
	// fill the row. Drag handlers use each tile's index in the FULL order so
	// reordering stays correct while some tiles are hidden.
	const fullOrder = (metricOrder.length ? metricOrder : METRIC_IDS).filter(
		(id) => id in METRIC_BY_ID
	)
	const order = fullOrder.filter((id) => !hiddenMetrics.includes(id))

	const selectMetric = (tileId: string, current: string, next: string) => {
		if (next === current) return
		// Uniqueness (swap with whichever tile already shows `next`) is enforced by
		// the store's setMetricCfg, so the drawer and this picker can't disagree.
		setMetricCfg(tileId, { metric: next })
	}

	return (
		<>
			<ReportSection
				title={sectionName}
				onTitleChange={(next) => setSectionName('metric', next)}
				onCustomize={() => setCustomizeOpen(true)}
			>
				{/* Dividers are direct children of the items-stretch row so they
			    self-stretch to the full tile height. In Customize mode each tile is
			    a drop zone and its icon becomes a drag grip. */}
				{order.length === 0 ? (
					// All tiles hidden → the section body would be empty. Show a
					// placeholder that also warns it's dropped from the export.
					<MetricsEmptyState reduced={reduced} />
				) : (
				<div className="flex items-stretch">
					{order.map((id, vi) => {
						// The tile shows its CONFIGURED metric (set in the customize drawer),
						// falling back to its own KPI by default.
						const cfg = metricConfig[id] ?? DEFAULT_METRIC_CONFIG[id]
						const m = METRIC_BY_ID[cfg?.metric] ?? METRIC_BY_ID[id]
						const i = fullOrder.indexOf(id)
						const dropActive =
							editMode &&
							dnd.overIndex === i &&
							dnd.dragIndex !== null &&
							dnd.dragIndex !== i
						return (
							<Fragment key={id}>
								{vi > 0 && <Divider orientation="vertical" />}
								<motion.div
									// Displaced siblings glide to their new slots when a tile is
									// dropped during reorder. `layout="position"` animates only the
									// x/y offset (never size — full `layout` scales and crushes the
									// tile text). Omitted when reduced so nothing tweens.
									{...(reduced ? {} : { layout: 'position' as const })}
									// HTML5 drag handlers (onDragStart/onDrop…) are plain DOM
									// attributes; their signatures collide with framer-motion's own
									// gesture props on motion.div (motion overrides onDrag* with pan
									// gesture types), so spread them untyped — they wire fine at
									// runtime as native drag events.
									// eslint-disable-next-line @typescript-eslint/no-explicit-any
									{...((editMode
										? { ...dnd.gripProps(i), ...dnd.zoneProps(i) }
										: {}) as any)}
									data-card-id={id}
									// First/last tiles carry the card's 12px outer corners so the
									// Concierge highlight ring traces them instead of poking out square.
									className={`relative min-w-0 flex-1 p-4 transition-colors ${
										vi === 0 ? 'rounded-l-xl' : ''
									} ${vi === order.length - 1 ? 'rounded-r-xl' : ''} ${
										editMode ? 'cursor-grab active:cursor-grabbing' : ''
									} ${dropActive ? 'bg-[#f4f3ff]' : ''} ${
										dnd.justMoved === i ? 'cg-drop-pop' : ''
									}`}
								>
									<CustomizeReveal
										show={editMode}
										delay={i * 45}
										className="absolute right-3 top-3"
									>
										<SlotToolGroup
											selectedMetric={m.label}
											showChartType={false}
											metricGroups={metricSections(METRIC_POOL)}
											onSelectMetric={(metric) =>
												selectMetric(id, m.label, metric)
											}
										/>
									</CustomizeReveal>
									{/* Crossfade the tile's contents when its metric changes (picked in
									    the customize drawer or the on-card tool group) so the value /
									    delta / icon swap reads as a quick lift, not an instant snap. The
									    drag grip (edit mode) stays put; only the metric content keys. */}
									<AnimatePresence mode="wait" initial={false}>
										<motion.div
											key={m.label}
											initial={reduced ? false : { opacity: 0, y: 6 }}
											animate={{ opacity: 1, y: 0 }}
											exit={reduced ? { opacity: 0 } : { opacity: 0, y: -6 }}
											transition={reduced ? { duration: 0 } : { duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
										>
											<MetricTile
												icon={editMode ? <DragGrip /> : undefined}
												label={m.label}
												// Trailing info icon + tooltip explaining the metric (same
												// muted info-circle as the chart headers). MetricTile portals
												// the bubble so it isn't clipped by the tray's overflow-hidden.
												// The DS Tooltip sizes to `w-max` capped at max-w-xs (320px),
												// which reads too wide for these sentences; cap the content
												// width so it wraps into a narrower, easier-to-read column
												// (scoped here so chart-header tooltips are unaffected).
												info={
													<span className="block max-w-[208px]">{m.tooltip}</span>
												}
												value={
													liveCards ? (
														formatMetricValue(liveCards[m.id]?.value, m.format, currency)
													) : (
														<Skeleton className="h-7 w-24" />
													)
												}
												change={
													liveCards ? (
														<Change
															size="sm"
															{...growthPillProps(
																m.id,
																liveCards[m.id]?.value,
																liveCards[m.id]?.prevValue
															)}
														/>
													) : (
														<Skeleton className="h-5 w-16" />
													)
												}
												note="vs prev period"
											/>
										</motion.div>
									</AnimatePresence>
								</motion.div>
							</Fragment>
						)
					})}
				</div>
				)}
			</ReportSection>
			<Suspense fallback={null}>
				<ChartCustomizationDrawer
					open={customizeOpen}
					onClose={() => setCustomizeOpen(false)}
					initialMode="metric"
				/>
			</Suspense>
		</>
	)
}
